import React, { useCallback, useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, Popup } from 'semantic-ui-react';
import _ from 'lodash';
import { useQuery } from '@tanstack/react-query';
import useSyncAllStudiesFromStoreWithPACSMutation, {
  useSyncAllStudiesFromStoreWithPACSMutationState,
} from 'app/queries/useSyncAllStudiesFromStoreWithPACSMutation';
import countFilteredStudiesQueryOptions from 'app/queries/countFilteredStudiesQueryOptions';
import { SyncStatusEnum, SyncStatusIcon } from 'app/components/SyncStatus';
import { PACSCommunicationContext } from 'app/components/PACSCommunicationProvider/context';
import { API } from 'app/utils/apiCalls';

type PACSSyncAllButtonProps = {
  filters?: API.StudiesFilter;
};

function PACSSyncAllButtonImpl({ filters }: PACSSyncAllButtonProps) {
  const { mutate } = useSyncAllStudiesFromStoreWithPACSMutation();
  const { status, variables } = _.last(useSyncAllStudiesFromStoreWithPACSMutationState()) ?? {};
  // If sync is ongoing, we display filter params of current sync

  const { data: syncedStudiesCount } = useQuery(
    countFilteredStudiesQueryOptions({
      ...filters,
      isPacsSynced: true,
    })
  );
  const { data: syncableStudiesCount } = useQuery(
    countFilteredStudiesQueryOptions({
      ...filters,
      isAcquisition: true,
    })
  );
  const syncStatus = useMemo(() => {
    if (status === 'pending') return SyncStatusEnum.ONGOING;
    if (status === 'error') return SyncStatusEnum.ERROR_SYNC;
    if (syncedStudiesCount === syncableStudiesCount) return SyncStatusEnum.SYNC;
    return SyncStatusEnum.NOT_SYNC;
  }, [syncedStudiesCount, syncableStudiesCount, status]);

  const triggerSyncAll = useCallback(() => {
    if (status === 'pending') {
      variables.abortController.abort();
      return;
    }
    const abortController = new AbortController();
    mutate({ abortController, filters });
  }, [mutate, filters, status, variables]);

  if (!syncableStudiesCount || !syncedStudiesCount) return null;

  const syncRate = `${Math.floor((syncedStudiesCount / syncableStudiesCount) * 100)}%`;

  return (
    <Popup
      trigger={
        <Button
          icon
          compact
          className={`pacs-sync-all picoxia ${syncStatus}`}
          onClick={triggerSyncAll}
          style={{ wordWrap: 'nowrap' }} // corrected 'styles' to 'style'
        >
          <SyncStatusIcon status={syncStatus} name="cloud upload" size="large" /> {syncRate}
        </Button>
      }
      content={
        <div>
          <h5>
            <FormattedMessage
              id="monitoring.actions.pacs.sync_all.description"
              values={{ savedNb: syncedStudiesCount, totalNb: syncableStudiesCount }}
            />
          </h5>
          {status === 'pending' ? (
            <FormattedMessage id="monitoring.actions.pacs.sync_all.trigger.cancel" />
          ) : (
            <FormattedMessage id="monitoring.actions.pacs.sync_all.trigger.save" />
          )}
        </div>
      }
    />
  );
}

export default function PACSSyncAllButton({ filters }: PACSSyncAllButtonProps) {
  const pacsCommunication = useContext(PACSCommunicationContext);
  if (!pacsCommunication) return null;

  return <PACSSyncAllButtonImpl filters={filters} />;
}
